import { GEO_MAX_ACCURACY_METERS, GEOLOCATION_POLICY } from '../constants';
import type { ContingencyReason, GeolocationPolicy, GeoStatus } from '../types';

type Location = { lat: number; lng: number };

/** Sensor-level outcome produced by `useGeolocation`, before any agreement rule applies. */
export interface GeoReading {
  location: Location | null;
  accuracy: number | null;
  status: GeoStatus;
  geoError: string | null;
  contingency: boolean;
  contingencyReason: ContingencyReason | null;
  blocked: boolean;
}

/** Geolocation state after the agreement's {@link GeolocationPolicy} is enforced. */
export interface GeoGate {
  location: Location | null;
  accuracy: number | null;
  contingency: boolean;
  contingencyReason: ContingencyReason | null;
  /** True when the punch must stay blocked until the condition is resolved. */
  blocked: boolean;
  /** True while the punch waits for a location the policy still needs. */
  awaitingLocation: boolean;
  /** Message to surface to the worker; `null` when there is nothing to report. */
  error: string | null;
}

const DISPENSED_GATE: GeoGate = {
  location: null,
  accuracy: null,
  contingency: false,
  contingencyReason: null,
  blocked: false,
  awaitingLocation: false,
  error: null,
};

function requiredPolicyError(reason: ContingencyReason, accuracy: number | null): string {
  if (reason === 'inaccurate') {
    return `Precisão da localização insuficiente (${Math.round(accuracy ?? 0)}m, máximo aceito ${GEO_MAX_ACCURACY_METERS}m): este acordo exige localização precisa, a coleta não será autorizada.`;
  }
  return 'Localização não obtida: este acordo exige localização, a coleta não será autorizada.';
}

function enforceRequired(reading: GeoReading): GeoGate {
  const { contingencyReason } = reading;
  return {
    location: reading.location,
    accuracy: reading.accuracy,
    contingency: false,
    contingencyReason: null,
    blocked: reading.blocked || contingencyReason !== null,
    awaitingLocation: !reading.location,
    error: contingencyReason ? requiredPolicyError(contingencyReason, reading.accuracy) : reading.geoError,
  };
}

function enforceFlexible(reading: GeoReading): GeoGate {
  return {
    location: reading.location,
    accuracy: reading.accuracy,
    contingency: reading.contingency,
    contingencyReason: reading.contingencyReason,
    blocked: reading.blocked,
    awaitingLocation: !reading.location && !reading.contingency,
    error: reading.geoError,
  };
}

/**
 * Applies the agreement's geolocation policy to a sensor reading.
 *
 * - required: contingency states (timeout, unavailable, inaccurate) block the punch
 * - flexible: contingency states let the punch proceed, flagged for audit
 * - dispensed: the reading is ignored and the punch never waits for a location
 *
 * @param policy - The agreement's geolocation policy.
 * @param reading - The sensor outcome from `useGeolocation`.
 * @returns The effective geolocation gate for the punch.
 */
export function applyGeolocationPolicy(policy: GeolocationPolicy, reading: GeoReading): GeoGate {
  if (policy === GEOLOCATION_POLICY.DISPENSED) return DISPENSED_GATE;
  if (policy === GEOLOCATION_POLICY.FLEXIBLE) return enforceFlexible(reading);
  return enforceRequired(reading);
}
